import { ReactNode, useEffect, useState } from "react";
import styles from "./LoadingCoverLayer.module.css";
import { createPortal } from "react-dom";

interface LoadingCoverLayerProps {
    isLoading: boolean;
    message?: string;
    onClose?: () => void;
    zIndex?: number;
    children?: ReactNode;
}

/**
 * 맵 화면 위에 로딩 레이어를 표시하는 컴포넌트
 * - 웹 전체 영역에 반투명 오버레이와 three-dots 로딩 애니메이션을 표시
 * - 분석 작업 중에 사용자 상호작용을 방지하고 로딩 상태를 시각적으로 표시
 * 
 * @author Kim Young Jun
 * @date 2025-07-03
 */
const LoadingCoverLayer = ({
    isLoading,
    message = "시간별 이동거리를 분석중입니다.",
    onClose,
    zIndex = 1000,
    children
}: LoadingCoverLayerProps) => {
    const [show, setShow] = useState(false);

    // 로딩 상태에 따라 애니메이션을 부드럽게 처리
    useEffect(() => {
        if (isLoading) {
            setShow(true);
        } else {
            const timer = setTimeout(() => {
                setShow(false);
            }, 500); // 페이드 아웃 애니메이션 시간
            return () => clearTimeout(timer);
        }
    }, [isLoading]);

    if (!show) return null;

    // createPortal을 사용하여 DOM의 최상위에 렌더링
    return createPortal(
        <div
            className={`${styles.overlayContainer} ${isLoading ? styles.visible : styles.hidden}`}
            style={{ zIndex }}
        >
            <div className={styles.overlay}>
                <div className={styles.content}>
                    <div className={styles.dotsContainer}>
                        {/* Three-dots 로딩 애니메이션 */}
                        <div className={styles.dotElastic}></div>
                    </div>
                    <p className={styles.message}>{message}</p>
                    {children}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default LoadingCoverLayer;